/* Hero carousel — auto-advance with progress bars and parallax.
 * Markup contract (rendered by sections/home-hero.liquid):
 *   <section class="hero-stage" data-hero-carousel data-slide-ms="6500">
 *     <div class="hero-stage__stack" data-hero-parallax> …slides… </div>
 *     <div class="hero-slide" data-active="true|false"> <img/> <video/> … </div>
 *     <div class="hero-nav"> <button data-hero-dot data-i="0"> … </button> </div>
 *     <div class="hero-copy" data-slide="0"> … </div>
 *   </section>
 */
(function () {
  document.querySelectorAll('[data-hero-carousel]').forEach((root) => {
    const slides = root.querySelectorAll('.hero-slide');
    const copies = root.querySelectorAll('.hero-copy');
    const dots = root.querySelectorAll('[data-hero-dot]');
    const videos = root.querySelectorAll('video');
    const stack = root.querySelector('[data-hero-parallax]');
    const SLIDE_MS = parseInt(root.dataset.slideMs || '6500', 10);
    let i = 0;

    // Device-aware hero video source. A slide with a DISTINCT mobile video carries
    // data-src-mobile / data-type-mobile. On phones (≤749px) we swap the <source>
    // to it — caching the desktop <source>s so we can restore on resize — so the
    // heavier desktop file is never fetched there. Slides without a mobile variant
    // keep their desktop <source> on every device (backward-compatible).
    const heroMobileMQ = window.matchMedia('(max-width: 749px)');
    const applyVideoSource = (v) => {
      const mSrc = v.dataset.srcMobile;
      if (!mSrc) return;
      if (v.dataset.desktopHtml == null) v.dataset.desktopHtml = v.innerHTML;
      const wantMobile = heroMobileMQ.matches;
      const onMobile = v.dataset.activeMode === 'mobile';
      if (wantMobile && !onMobile) {
        const s = document.createElement('source');
        s.src = mSrc;
        s.type = v.dataset.typeMobile || 'video/mp4';
        v.innerHTML = '';
        v.appendChild(s);
        v.dataset.activeMode = 'mobile';
        v.load();
      } else if (!wantMobile && onMobile) {
        v.innerHTML = v.dataset.desktopHtml;
        v.dataset.activeMode = 'desktop';
        v.load();
      }
    };

    // iOS Safari requires these as runtime properties — HTML attributes
    // alone aren't enough to keep autoplay alive across programmatic
    // pause/play cycles. Set on every video at init. Pick the device source
    // BEFORE any .play() so the wrong file never starts downloading.
    videos.forEach((v) => {
      v.muted = true;
      v.defaultMuted = true;
      v.playsInline = true;
      v.setAttribute('muted', '');
      v.setAttribute('playsinline', '');
      applyVideoSource(v);
    });

    // Re-pick + resume the active slide if the viewport crosses the breakpoint.
    const onHeroMQChange = () => {
      videos.forEach(applyVideoSource);
      const active = videos[i];
      if (active && slides[i] && slides[i].dataset.active === 'true') {
        const p = active.play();
        if (p && p.catch) p.catch(() => {});
      }
    };
    if (heroMobileMQ.addEventListener) heroMobileMQ.addEventListener('change', onHeroMQChange);
    else if (heroMobileMQ.addListener) heroMobileMQ.addListener(onHeroMQChange);
    // Helper: lazy-attach a slide's poster <img> if it was deferred.
    // Slides 2+ ship with `data-lazy-src` / `data-lazy-srcset` instead of
    // `src` / `srcset` to keep them out of the initial network payload
    // (browsers ignore loading="lazy" for absolutely-positioned images
    // stacked in the initial viewport, so we have to JS-defer them).
    const ensureSlideImage = (slideEl) => {
      if (!slideEl) return;
      // <picture> mobile <source> defers its srcset on slides 2+ too — set it
      // BEFORE the <img> so the picture evaluates the right candidate at once.
      const psrc = slideEl.querySelector('picture source[data-lazy-srcset]');
      if (psrc) {
        psrc.srcset = psrc.dataset.lazySrcset;
        psrc.removeAttribute('data-lazy-srcset');
      }
      const img = slideEl.querySelector('img[data-lazy-src]');
      if (!img) return;
      if (img.dataset.lazySrcset) img.srcset = img.dataset.lazySrcset;
      if (img.dataset.lazySizes)  img.sizes  = img.dataset.lazySizes;
      img.src = img.dataset.lazySrc;
      img.removeAttribute('data-lazy-src');
      img.removeAttribute('data-lazy-srcset');
      img.removeAttribute('data-lazy-sizes');
    };

    // Defer slide 1's video .play() until the page has had a chance to paint
    // its LCP image. Without this delay the video frame becomes the LCP
    // candidate and desktop LCP balloons to ~5s. requestIdleCallback (or a
    // 200ms setTimeout fallback) lets LCP fire first, then video catches up.
    const startFirstVideo = () => {
      if (videos[0]) {
        const p = videos[0].play();
        if (p && p.catch) p.catch(() => {});
      }
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(startFirstVideo, { timeout: 600 });
    } else {
      setTimeout(startFirstVideo, 200);
    }

    function go(next) {
      i = (next + slides.length) % slides.length;
      slides.forEach((s, idx) => (s.dataset.active = idx === i ? 'true' : 'false'));
      copies.forEach((c, idx) => (c.dataset.active = idx === i ? 'true' : 'false'));
      dots.forEach((d, idx) => (d.dataset.active = idx === i ? 'true' : 'false'));
      // Lazy-load the new active slide's poster image if it hasn't loaded yet.
      ensureSlideImage(slides[i]);
      // Also pre-warm the NEXT slide's image so the rotation transition
      // doesn't show a half-second flash of empty background.
      ensureSlideImage(slides[(i + 1) % slides.length]);
      videos.forEach((v, idx) => {
        try {
          if (idx === i) {
            v.muted = true;
            v.playsInline = true;
            if (v.readyState >= 1) v.currentTime = 0;
            const p = v.play();
            if (p && p.catch) p.catch(() => {});
          } else {
            v.pause();
          }
        } catch (_) {}
      });
    }

    dots.forEach((d) => d.addEventListener('click', () => go(parseInt(d.dataset.i, 10))));

    if (slides.length > 1) setInterval(() => go(i + 1), SLIDE_MS);

    /* Parallax on scroll — intensity driven by schema setting
       (data-parallax on the section root). */
    const parallaxIntensity = root.dataset.parallax || 'subtle';
    const parallaxMul = { none: 0, subtle: 0.18, strong: 0.30 }[parallaxIntensity] ?? 0.18;
    const scaleMul    = { none: 0, subtle: 0.0002, strong: 0.0004 }[parallaxIntensity] ?? 0.0002;
    if (stack && parallaxMul > 0) {
      const onScroll = () => {
        const y = window.scrollY;
        stack.style.transform = `translate3d(0, ${y * parallaxMul}px, 0) scale(${1 + y * scaleMul})`;
      };
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    go(0);
  });
})();
